import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import type { LocaleCode } from '@cobliteam/i18n';
import { useLanguage } from '@cobliteam/i18n';
import { Button, DropdownMenu } from '@cobliteam/ui/components';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { useState } from 'react';

const languageOptions: Array<{ code: LocaleCode; label: string; flag: string }> = [
  { code: 'pt-BR', label: 'Português', flag: '🇧🇷' },
  { code: 'en-US', label: 'English', flag: '🇺🇸' },
];

/**
 * Language switcher component using Cobli UI DropdownMenu
 * Can be used in header, settings, or anywhere language switching is needed
 */
export function LanguageSwitcher() {
  const { currentLanguage, changeLanguage } = useLanguage();
  const [open, setOpen] = useState(false);

  const handleLanguageSelect = async (code: LocaleCode) => {
    await changeLanguage(code);
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <Button variant='outlined' color='default' size='sm' icon={<Icon name='globe' size='md' />} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        <div className='py-2 text-gray-500 text-sm'>⚠️ Visível apenas para Cobler ⚠️</div>
        <DropdownMenu.Separator />
        <DropdownMenu.Group>
          {languageOptions.map((option) => (
            <DropdownMenu.Item key={option.code} onClick={() => handleLanguageSelect(option.code)} className={cn('cursor-pointer', option.code === currentLanguage && 'bg-gray-2')}>
              <DropdownMenu.Icon icon={<span className='text-lg'>{option.flag}</span>} />
              <DropdownMenu.Label>{option.label}</DropdownMenu.Label>
              {option.code === currentLanguage && <Icon name='check' size='sm' />}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Group>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}

/**
 * Simple language toggle button (PT/EN) using Cobli UI Button
 */
export function LanguageToggle({ className = '' }: { className?: string }) {
  const featureFlags = useFeatureFlags();
  const { currentLanguage, changeLanguage } = useLanguage();

  if (!featureFlags.cobler) return null;

  const toggleLanguage = async () => {
    const newLanguage = currentLanguage === 'pt-BR' ? 'en-US' : 'pt-BR';
    await changeLanguage(newLanguage);
  };

  return (
    <Button
      variant='ghost'
      size='sm'
      onClick={toggleLanguage}
      className={cn('flex items-center gap-1', className)}
      aria-label={`Mudar para ${currentLanguage === 'pt-BR' ? 'Inglês' : 'Português'}`}
    >
      <span className='text-lg'>{currentLanguage === 'pt-BR' ? '🇺🇸' : '🇧🇷'}</span>
      <span>{currentLanguage === 'pt-BR' ? 'EN' : 'PT'}</span>
    </Button>
  );
}
